import * as React from 'react';
import { useHistory } from 'react-router';
import { Authority, AuthorityLabel } from '../components/AuthorityLabel';
import { Button } from '../components/Button';
import {
  ConfirmDialog,
  GraphQLErrorDialog,
} from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { PageHeader } from '../components/PageHeader';
import { ToastContext } from '../components/Toast';
import {
  useDeleteAccountMutation,
  useGetAccountsLazyQuery,
  useUpdateAccountsDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import './AccountListPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';
import { LIST_SEARCH_EMPTY_MESSAGE, useListSearch } from '../components/ListSearch';

interface DeleteAccountTarget {
  id: number;
  screen_id: string;
  name: string;
}

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 5;

/**
 * ユーザーの編集に必要な権限の段階。
 * この一覧の編集・削除は、管理（/admin配下）の画面を開ける管理者以上に出しており、
 * サーバーの並び順の保存（updateAccountsDisplayOrder）も管理者以上なので、「並び替え」も同じ段階にする
 */
const EDIT_AUTHORITY_LEVEL = Authority.Admin;

/**
 * ユーザー（アカウント）の一覧
 */
export const AccountListPage: React.FC = function () {
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  const [deleteTarget, setDeleteTarget] = React.useState<DeleteAccountTarget>();
  const [getAccounts, getAccountsResult] = useGetAccountsLazyQuery();
  const [deleteAccount, deleteAccountResult] = useDeleteAccountMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getAccounts();
    },
    onError() {
      // 削除に失敗しても一覧は残し、続けて操作できるようにする
      toastContext.showToast('削除できませんでした。時間をおいて再実行してください。', 'error');
    },
  });
  // 見出しの右の絞り込みの欄。絞り込んでいる間は並びを変えさせない
  const search = useListSearch();
  const [updateDisplayOrder] = useUpdateAccountsDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getAccountsResult.data?.getAccounts ?? []).map((account) => account.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: search.filtering,
    save: (ids) => updateDisplayOrder({ variables: { accountIds: ids } }),
  });

  if (!getAccountsResult.called) {
    getAccounts();
  }

  if (getAccountsResult.error) {
    return <GraphQLErrorDialog error={getAccountsResult.error} />;
  }

  if (
    getAccountsResult.loading ||
    !getAccountsResult.data ||
    deleteAccountResult.loading
  ) {
    return <Loading />;
  }

  const accounts = getAccountsResult.data.getAccounts;
  const displayedAccounts = orderByIds(accounts, reorderMode.displayedIds).filter((account) =>
    search.matches([account.name, account.screen_id, account.department])
  );

  let rows;
  if (displayedAccounts.length === 0) {
    // 1件も登録されていないときと、絞り込みで1件も残らなかったときで、出す文を分ける
    let emptyMessage = LIST_SEARCH_EMPTY_MESSAGE;
    if (accounts.length === 0) {
      emptyMessage = 'ユーザーが登録されていません';
    }
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message={emptyMessage} />;
  } else {
    rows = displayedAccounts.map((a, index) => {
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(a.id, index)} />;
      }
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <Button
              mini={true}
              color="white"
              onClick={() => history.push(`/admin/accounts/${a.id}/edit`)}
            >
              編集
            </Button>
            <Button mini={true} color="red" onClick={() => setDeleteTarget(a)}>
              削除
            </Button>
          </div>
        </td>
      );

      return (
        <tr key={a.id} {...reorderMode.getRowProps(a.id)}>
          {handleCell}
          <td>{a.name}</td>
          <td>{a.screen_id}</td>
          <td>{a.department}</td>
          <td>
            <AuthorityLabel authority={a.authority.level} />
          </td>
          {actionCell}
        </tr>
      );
    });
  }

  let deleteDialog;
  if (deleteTarget) {
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="ユーザーの削除"
        message={
          <p>
            {deleteTarget.name}（{deleteTarget.screen_id}）を削除しますか？
          </p>
        }
        yesLabel="削除"
        onYes={() => {
          deleteAccount({ variables: { accountId: deleteTarget.id } });
          setDeleteTarget(undefined);
        }}
        onNo={() => setDeleteTarget(undefined)}
      />
    );
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <Button mini={true} onClick={() => history.push('/admin/accounts/new')}>
        新規作成
      </Button>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th className="App-table_actions_column">操作</th>;

  // 列の幅は列の位置ごとに保存されるため、列の並びを変えたときは保存の名前も変え、以前の並びで保存した幅を別の列に当てないようにする
  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'accountsByName');

  return (
    <div className="AccountListPage">
      {deleteDialog}
      <PageHeader
        title="ユーザー一覧"
        breadcrumbs={[{ label: '管理画面' }, { label: 'ユーザー一覧' }]}
        actions={headerActions}
      />
      <ListSummary
        reorder={reorderMode}
        shownCount={displayedAccounts.length}
        search={{ search, placeholder: '名前・ID・所属部署で絞り込み' }}
      />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table App-table_full">
          <thead>
            <tr>
              {handleHeader}
              <th>名前</th>
              <th>ID</th>
              <th>所属部署</th>
              <th>権限</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
